import { useEffect, useMemo, useState } from "react";

import { type ReportConfig } from "@doitintl/cmp-models";

import { errorsText, reportTxt } from "../../../assets/texts/CloudAnalytics";
import { AvaQuickPrompt } from "../../../Components/Ava2/AvaQuickPrompt";
import { WorkflowsEnum } from "../../../Components/Ava2/types";
import { useAva } from "../../../Components/Ava2/useAva";
import { consoleErrorWithSentry } from "../../../utils";
import { useReportConfig } from "../Context";
import { type AvaReportConfig, mapAvaConfigToReportConfig } from "../utilities";

type AvaReportPromptProps = {
  isGeneratingReportConfig: boolean;
  onIsGeneratingReportConfig: (isGeneratingReportConfig: boolean) => void;
};

export const AvaReportPrompt = ({ isGeneratingReportConfig, onIsGeneratingReportConfig }: AvaReportPromptProps) => {
  const [errorDescription, setErrorDescription] = useState<string | undefined>(undefined);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const { updateConfigFromGeneration } = useReportConfig();
  const { askQuestion, answer, onStopGeneration, answerExtras } = useAva(
    () => {
      onIsGeneratingReportConfig(false);
    },
    "console",
    () => false
  );

  const parsedConfig = useMemo<ReportConfig | null>(() => {
    try {
      if (!answer || typeof answer !== "string" || answer.trim() === "") {
        return null;
      }

      if (answer.includes("invalid")) {
        setErrorDescription(errorsText.AVA_ERROR.DESCRIPTION);
        onIsGeneratingReportConfig(false);
        return null;
      }

      let processedAnswer = answer;
      if (answer.startsWith("valid")) {
        processedAnswer = answer.replace(/^valid/, "").trim();
      }

      const parsedAnswer: { reportConfig: AvaReportConfig; error?: string } = JSON.parse(processedAnswer);

      if (!parsedAnswer.reportConfig) {
        return null;
      }

      return mapAvaConfigToReportConfig(parsedAnswer.reportConfig);
    } catch (error) {
      return null;
    }
  }, [answer, onIsGeneratingReportConfig]);

  useEffect(() => {
    if (!parsedConfig) {
      return;
    }

    updateConfigFromGeneration(parsedConfig);
    onIsGeneratingReportConfig(false);
  }, [parsedConfig, updateConfigFromGeneration, onIsGeneratingReportConfig]);

  const handleAskQuestion = async (question: string) => {
    onIsGeneratingReportConfig(true);
    setErrorDescription(undefined);
    try {
      await askQuestion({
        question,
        conversationId,
        conversationCallback: (conversationId) => {
          setConversationId(conversationId);
        },
        flow: WorkflowsEnum.AnalyticsReports,
      });
    } catch (error) {
      consoleErrorWithSentry(error);
      setErrorDescription(errorsText.AVA_ERROR.DESCRIPTION);
      onIsGeneratingReportConfig(false);
    }
  };

  return (
    <AvaQuickPrompt
      title={reportTxt.AVA_PROMPT_TITLE}
      description={reportTxt.AVA_PROMPT_DESCRIPTION}
      isLoading={isGeneratingReportConfig}
      disabled={isGeneratingReportConfig}
      showStopButton={isGeneratingReportConfig && !!answerExtras?.answerId}
      onAskQuestionClicked={handleAskQuestion}
      showFileAttachment={false}
      showGeneratingIndicator={false}
      error={
        errorDescription
          ? {
              description: errorDescription,
            }
          : undefined
      }
      handleStopGeneration={() => {
        if (conversationId && answerExtras?.answerId) {
          onStopGeneration(conversationId, answerExtras?.answerId, answer);
          onIsGeneratingReportConfig(false);
        }
      }}
    />
  );
};
